import { useState } from "react";
import { Database, BarChart3, LogOut, Globe, Menu, X, FlaskConical, History, Coins, Users, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";

const ROLE_LABELS = { admin: "Admin", analyst: "Analyst", viewer: "Nur lesen" };

export const DashboardLayout = ({ children, onLogout, activeTab, onTabChange, currentUser }) => {
  const user = currentUser?.username || "";
  const isAdmin = currentUser?.role === "admin";
  const [mobileOpen, setMobileOpen] = useState(false);

  const tabs = [
    { id: "crawler",   label: "Crawler",     icon: Globe },
    { id: "domains",   label: "Domains",     icon: Database },
    { id: "research",  label: "Research",    icon: FlaskConical },
    { id: "analytics", label: "Analytics",   icon: BarChart3 },
    { id: "jobs",      label: "Job History", icon: History },
    { id: "tokens",    label: "Token Usage", icon: Coins },
    ...(isAdmin ? [
      { id: "users", label: "Benutzer",  icon: Users },
      { id: "audit", label: "Audit-Log", icon: ScrollText },
    ] : []),
  ];

  const handleTabChange = (id) => {
    onTabChange?.(id);
    setMobileOpen(false);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row" data-testid="dashboard-layout">

      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border">
        <div>
          <h2 className="text-lg font-black tracking-tight text-foreground" style={{ fontFamily: 'DM Sans Variable, DM Sans, sans-serif' }}>
            Crawler<span className="inline-block w-[0.2em] h-[0.2em] rounded-full bg-[#DBE6EC] ml-[0.06em] align-baseline" style={{ marginBottom: '0.15em' }}></span>
          </h2>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            <span className="font-light">by</span> <span className="font-bold tracking-wide">OFFGROUND</span>
          </p>
        </div>
        <Button
          data-testid="mobile-menu-btn"
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="text-muted-foreground hover:text-foreground h-8 w-8"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile Dropdown Nav */}
      {mobileOpen && (
        <div className="md:hidden bg-card border-b border-border px-4 py-3 space-y-1">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`mobile-nav-${id}`}
              onClick={() => handleTabChange(id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-[var(--radius)] text-sm font-medium text-left transition-colors ${
                activeTab === id
                  ? 'bg-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary))] font-semibold'
                  : 'text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--primary)/0.08)]'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
          <div className="flex items-center justify-between px-3 py-2 mt-2 border-t border-border">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-[var(--radius)] bg-[hsl(var(--primary))] flex items-center justify-center text-[10px] font-bold text-[hsl(var(--primary-foreground))]">
                {(user[0] || "?").toUpperCase()}
              </div>
              <span className="text-sm text-muted-foreground">{user}</span>
            </div>
            <Button
              data-testid="mobile-logout-btn"
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="h-7 text-muted-foreground hover:text-foreground text-xs"
            >
              <LogOut className="w-3.5 h-3.5 mr-1" /> Logout
            </Button>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-56 border-r border-border flex-col shrink-0" data-testid="sidebar">
        <div className="p-5 border-b border-border">
          <h2 className="text-2xl font-black tracking-tight text-foreground" style={{ fontFamily: 'DM Sans Variable, DM Sans, sans-serif' }}>
            Crawler<span className="inline-block w-[0.22em] h-[0.22em] rounded-full bg-[#DBE6EC] ml-[0.08em] align-baseline" style={{ marginBottom: '0.18em' }}></span>
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            <span className="font-light">by</span> <span className="font-bold tracking-wide">OFFGROUND</span>
          </p>
        </div>

        <nav className="flex-1 p-3 space-y-0.5">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`nav-${id}`}
              onClick={() => onTabChange?.(id)}
              className={`sidebar-link w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-left ${
                activeTab === id ? 'active' : ''
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-7 h-7 rounded-[var(--radius)] bg-[hsl(var(--primary))] flex items-center justify-center text-xs font-bold text-[hsl(var(--primary-foreground))]">
              {(user[0] || "?").toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{user}</p>
              <p className="text-[10px] text-muted-foreground">{ROLE_LABELS[currentUser?.role] || ""}</p>
            </div>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="icon"
              onClick={onLogout}
              className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--primary)/0.08)]"
            >
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
};
